import { t } from '@lingui/macro'
import { useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { mixpanel } from '@/mixpanel'
import { DoctorInfo } from '@/modules/appointment/components/doctor-info/DoctorInfo'
import { formatFieldOfSpecialisations } from '@/modules/appointment/helpers/doctor'
import { useDoctors } from '@/modules/appointment/hooks/useDoctors'
import { StorageKeys, StorageService } from '@/utils/storageService'
import { CareQuestionDetailContentLayout } from '../../../components/care-question-detail-content-layout/CareQuestionDetailContentLayout'
import { useCareQuestionContext } from '../../../context/CareQuestion.context'

import type { AppHeaderProps } from '@/@bothrs-ui/web/components/navigation/header/AppHeader.types'
import type { ESignDoctor } from '@/modules/appointment/types/e-sign.types'

type Props = {
  header?: AppHeaderProps
  progress?: { min: number; max: number; value: number }
  title?: string
  description?: string
  onNext: (doctor: ESignDoctor) => void
}

export const DoctorStep = ({
  header,
  progress,
  description = t({
    id: 'care-question.question-flow.doctor.description',
    message: 'Kies één van je recente artsen om een medische vraag te stellen.',
  }),
  onNext,
}: Props) => {
  // State
  // ------------------------------------------------------------------------- /
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null)

  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const { questionData, setQuestionData, specialisations, doctors: contextDoctors } = useCareQuestionContext()
  const specialisation = questionData?.specialisation as { id: number }

  const { data: getDoctorsValue, mutate: getDoctors } = useDoctors(specialisation?.id)

  // Template variables
  // ------------------------------------------------------------------------- /
  const doctors = useMemo(() => {
    const baseDoctors = specialisation?.id
      ? specialisations?.find((s: any) => s.id === specialisation.id)?.doctors || []
      : contextDoctors || []

    return baseDoctors.map((doctor: any) => {
      const esignDoctor = getDoctorsValue?.find((esignDoctor) => esignDoctor.agendaId === doctor.agendaId)

      return {
        ...esignDoctor,
        id: doctor.id,
        agendaId: doctor.agendaId,
        name: esignDoctor?.name || doctor.name,
      } as ESignDoctor
    })
  }, [specialisation, specialisations, contextDoctors, getDoctorsValue])

  const handleContinue = () => {
    if (selectedDoctorId === null) return

    const selectedDoctor = doctors.find((d: any) => d.id === selectedDoctorId)

    if (!selectedDoctor) return

    mixpanel.track('care_question_doctor_selected', {
      doctorId: selectedDoctor.id,
      doctorName: selectedDoctor.name,
    })

    const doctorInContext = contextDoctors?.find((d: any) => d.id === selectedDoctor.id)

    const currentSpecId = questionData.specialisationId as number
    let nextSpecId = currentSpecId

    if (!nextSpecId && doctorInContext?.specialisms?.length) {
      // Use the first specialism id if available
      nextSpecId =
        (doctorInContext.specialisms[0] as unknown as { id: number }).id ||
        (doctorInContext.specialisms[0] as unknown as number)
    }

    setQuestionData((previous: any) => ({
      ...previous,
      doctorId: selectedDoctor.id,
      specialisationId: nextSpecId,
    }))

    onNext(selectedDoctor)
  }

  // Effects
  // ------------------------------------------------------------------------- /

  useEffect(() => {
    StorageService.getItem(StorageKeys.PreSelectedDoctor).then(async (preSelectedDoctor) => {
      if (preSelectedDoctor) {
        await StorageService.removeItem(StorageKeys.PreSelectedDoctor)

        const parsedPreSelectedDoctor = Number.parseInt(preSelectedDoctor, 10)
        if (parsedPreSelectedDoctor && !Number.isNaN(parsedPreSelectedDoctor)) {
          setSelectedDoctorId(parsedPreSelectedDoctor)
        }
      }
    })

    return () => {
      setSelectedDoctorId(null)
      StorageService.removeItem(StorageKeys.PreSelectedDoctor)
    }
  }, [doctors])

  useEffect(() => {
    getDoctors()
  }, [getDoctors])

  // Render
  // ------------------------------------------------------------------------- /
  return (
    <CareQuestionDetailContentLayout
      header={header}
      progress={progress}
      bottom={
        <Button variant='Primary' onClick={handleContinue} disabled={selectedDoctorId === null}>
          {t({
            id: 'global.next',
            message: 'Volgende',
          })}
        </Button>
      }
    >
      <StScrollContainer>
        <FlexColumn gap={theme.UI.SpacingPx.Space6}>
          <FlexColumn gap={theme.UI.SpacingPx.Space4}>
            <Span type='BodyLargeRegular'>{description}</Span>
          </FlexColumn>

          <FlexColumn gap={theme.UI.SpacingPx.Space4}>
            {doctors.map((doctor) => {
              const isSelected = selectedDoctorId === doctor.id

              const specialisms = doctor.specialisms as unknown as {
                agendaId: string
                id: number
                title: { nl: string; en: string }
              }[]

              const description = specialisms?.map((specialism) => specialism?.title?.nl).join(', ')
              const fields = formatFieldOfSpecialisations(doctor.fieldOfSpecialisations)

              console.log(doctor, specialisations)

              return (
                <StCard key={doctor.id} selected={isSelected} onClick={() => setSelectedDoctorId(doctor.id)}>
                  <DoctorInfo>
                    <DoctorInfo.Avatar images={doctor.images} name={doctor.name} size='large' />
                    <DoctorInfo.Details>
                      <DoctorInfo.Name type='BodyLargeBold'>{doctor.name}</DoctorInfo.Name>
                      {description ? <DoctorInfo.Description>{description}</DoctorInfo.Description> : null}
                      {fields ? <DoctorInfo.Note>{fields}</DoctorInfo.Note> : null}
                      {doctor.conventionStatus?.nl ? (
                        <DoctorInfo.Note>{doctor.conventionStatus.nl}</DoctorInfo.Note>
                      ) : null}
                    </DoctorInfo.Details>
                  </DoctorInfo>
                </StCard>
              )
            })}
          </FlexColumn>
        </FlexColumn>
      </StScrollContainer>
    </CareQuestionDetailContentLayout>
  )
}

const StScrollContainer = styled.div`
  overflow-y: auto;
  flex-grow: 1;
  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space10};
`

const StCard = styled(Card)<{ selected: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`};
  border: 1px solid ${({ theme }) => theme.Components.Conversational.Border};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;

  transition-property: border, background, color;
  transition-duration: 175ms;
  transition: all 175ms ease-out;

  border-color: ${({ theme, selected }) =>
    selected ? theme.Components.Conversational.ActiveBorder : theme.Components.Conversational.Border};
  background-color: ${({ theme, selected }) =>
    selected ? theme.Components.Conversational.ActiveFill : theme.Components.Conversational.Fill};
  color: ${({ theme, selected }) =>
    selected ? theme.Components.Conversational.ActiveText : theme.Components.Conversational.Text};
`
